import { useEffect, useState } from "react";
import { Sparkles, Zap, X, Loader2, ArrowUpRight, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";

/**
 * Sprint 904 — Credit Depletion Modal.
 *
 * Listens to the global `credit-depleted` window event (fired by api.js
 * when the backend returns 403 with code INSUFFICIENT_CREDITS or
 * MONTHLY_LIMIT_EXCEEDED) and shows a tier-aware add-on recommendation
 * with a one-click purchase CTA.
 *
 * Mounted ONCE in App.js so any blocked AI call from anywhere in the
 * app surfaces the same dialog.
 */
export default function CreditDepletionModal() {
  const [detail, setDetail] = useState(null);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    const onDepleted = (e) => setDetail(e.detail || {});
    window.addEventListener("credit-depleted", onDepleted);
    return () => window.removeEventListener("credit-depleted", onDepleted);
  }, []);

  if (!detail) return null;

  const recommended = detail.recommended_package;
  const allPackages = detail.all_packages || [];
  const isMonthly = detail.code === "MONTHLY_LIMIT_EXCEEDED" || detail.error === "monthly_limit_exceeded";
  const titleText = isMonthly ? "Monthly AI limit reached" : "Out of AI credits";
  const subText = isMonthly
    ? `You've used your full monthly allowance (${(detail.used || 0).toLocaleString()} / ${(detail.limit || 0).toLocaleString()}).`
    : `Balance: ${(detail.balance || 0).toLocaleString()} credits · this action needs ${(detail.cost || 0).toLocaleString()}.`;

  const close = () => {
    // Sprint 931 — fire-and-forget abandonment track if user closes WITHOUT buying.
    // Backend schedules a 30-min recovery email with 10% off coupon.
    try {
      if (detail && detail.recommended_package) {
        api.post("/ai-credits/abandonment/track", {
          operation: detail.operation || "ai_generation",
          recommended_package: detail.recommended_package,
        }).catch(() => {});
      }
    } catch (_e) { /* never break close */ }
    setDetail(null);
  };

  const buy = async (pkg) => {
    setPurchasing(true);
    try {
      const r = await api.post("/ai-credits/purchase", {
        package_id: pkg.id,
        origin_url: window.location.origin,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[credit-purchase] failed", err);
      setPurchasing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      data-testid="credit-depletion-modal"
      onClick={close}
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-[var(--app-surface)] border border-[var(--app-border)] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Gradient header strip */}
        <div className="h-1.5 bg-gradient-to-r from-violet-500 via-pink-500 to-amber-500" />

        <button
          onClick={close}
          className="absolute top-3 right-3 p-1.5 rounded-lg text-[var(--app-text-muted)] hover:bg-[var(--app-surface-hover)]"
          data-testid="credit-modal-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 space-y-5">
          {/* Icon + title */}
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500/15 to-pink-500/15 flex items-center justify-center flex-shrink-0">
              {isMonthly ? (
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              ) : (
                <Zap className="w-5 h-5 text-violet-400" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-bold leading-tight" data-testid="credit-modal-title">
                {titleText}
              </h2>
              <p className="text-xs text-[var(--app-text-muted)] mt-1 leading-relaxed">{subText}</p>
            </div>
          </div>

          {/* Recommended package — primary CTA */}
          {recommended && (
            <div
              className="rounded-xl border-2 border-violet-500/40 bg-gradient-to-br from-violet-500/8 to-pink-500/8 p-4 space-y-3"
              data-testid="credit-modal-recommended"
            >
              <div className="flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-violet-400" />
                <span className="text-[10px] font-bold tracking-wider uppercase text-violet-400">
                  Recommended for you
                </span>
              </div>
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-2xl font-extrabold tracking-tight" data-testid="credit-modal-rec-credits">
                    {recommended.credits.toLocaleString()} credits
                  </div>
                  <div className="text-[11px] text-[var(--app-text-muted)] mt-0.5">
                    {recommended.name || "Top-up pack"}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-extrabold text-violet-400">
                    USD ${recommended.price}
                  </div>
                  <div className="text-[10px] text-[var(--app-text-muted)]">one-time</div>
                </div>
              </div>
              <button
                onClick={() => buy(recommended)}
                disabled={purchasing}
                className="w-full py-2.5 rounded-lg bg-gradient-to-r from-violet-600 to-pink-600 hover:opacity-90 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="credit-modal-buy-recommended"
              >
                {purchasing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
                Buy {recommended.credits.toLocaleString()} credits
              </button>
            </div>
          )}

          {/* Secondary packs */}
          {allPackages.length > 1 && (
            <div className="space-y-2" data-testid="credit-modal-other-packs">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--app-text-muted)]">
                Other top-up packs
              </div>
              {allPackages
                .filter((p) => p.id !== recommended?.id)
                .map((p) => (
                  <button
                    key={p.id}
                    onClick={() => buy(p)}
                    disabled={purchasing}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-[var(--app-border)] hover:border-violet-500/40 hover:bg-[var(--app-surface-hover)] transition-colors disabled:opacity-50"
                    data-testid={`credit-modal-buy-${p.id}`}
                  >
                    <span className="text-sm font-medium">{p.credits.toLocaleString()} credits</span>
                    <span className="text-sm font-bold text-violet-400">USD ${p.price}</span>
                  </button>
                ))}
            </div>
          )}

          <div className="text-[11px] text-[var(--app-text-muted)] text-center pt-1">
            Need more? Visit{" "}
            <a
              href="/settings"
              className="text-violet-400 font-semibold hover:underline"
              data-testid="credit-modal-billing-link"
            >
              Settings → Billing & Plans
            </a>{" "}
            to upgrade your tier.
          </div>
        </div>
      </div>
    </div>
  );
}
